import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import './UserStatus.css';

/**
 * UserStatus Component
 * Displays a status check UI with a colored status dot
 */
function UserStatus({ onBack }) {
  const [statusColor, setStatusColor] = useState('white');
  const [walletAddress, setWalletAddress] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, loading, ready_to_sign, signing, success, error
  const [error, setError] = useState('');
  const [lastChecked, setLastChecked] = useState(null);
  const [isCached, setIsCached] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Get configuration from environment variables
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const webApiCode = process.env.REACT_APP_WEB_API_CODE || '';

  /**
   * Check if user has completed a status check before
   */
  useEffect(() => {
    const hasChecked = localStorage.getItem('hasCheckedStatus');
    if (hasChecked === 'true') {
      setTermsAccepted(true);
    }
  }, []);

  /**
   * Check if this is the first time (no localStorage set yet)
   */
  const isFirstTime = () => {
    return localStorage.getItem('hasCheckedStatus') !== 'true';
  };

  /**
   * Check for connected wallet on mount
   */
  useEffect(() => {
    const checkWalletConnection = async () => {
      if (window.ethereum) {
        try {
          const provider = new ethers.BrowserProvider(window.ethereum);
          const accounts = await provider.listAccounts();
          if (accounts.length > 0) {
            const address = await accounts[0].getAddress();
            setWalletAddress(address);
          }
        } catch (err) {
          console.log('No wallet connected yet');
        }
      }
    };
    
    checkWalletConnection();
  }, []);

  /**
   * Listen for MetaMask account changes
   */
  useEffect(() => {
    if (window.ethereum) {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length > 0) {
          setWalletAddress(accounts[0]);
          // Reset status when wallet changes
          resetStatus();
        } else {
          setWalletAddress('');
          resetStatus();
        }
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      };
    }
  }, []);

  /**
   * Reset status to initial state
   */
  const resetStatus = () => {
    setStatusColor('white');
    setChallenge(null);
    setStatus('idle');
    setError('');
    setLastChecked(null);
    setIsCached(false);
  };

  /**
   * Handle wallet connection
   */
  const handleConnectWallet = async () => {
    try {
      if (!window.ethereum) {
        setError('MetaMask is not installed. Please install MetaMask to continue.');
        return;
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      await provider.send("eth_requestAccounts", []);
      const signer = await provider.getSigner();
      const address = await signer.getAddress();
      setWalletAddress(address);
      setError('');
    } catch (err) {
      if (err.code === 4001) {
        setError('Wallet connection was rejected');
      } else {
        setError('Failed to connect wallet: ' + err.message);
      }
    }
  };

  /**
   * Handle status check - request challenge from backend
   */
  const handleCheckStatus = async () => {
    try {
      setStatus('loading');
      setError('');
      
      if (!walletAddress) {
        setError('Please connect your wallet first');
        setStatus('idle');
        return;
      }

      // Save that user has completed a status check
      if (!localStorage.getItem('hasCheckedStatus')) {
        localStorage.setItem('hasCheckedStatus', 'true');
      }

      // Request challenge from backend
      const response = await fetch(`${apiUrl}/api/WebQueryStatus?code=${webApiCode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          FunctionArgument: {
            RequestType: 'GetStatusChallenge',
            wallet_address: walletAddress
          }
        })
      });

      const data = await response.json();

      if (data.Reason === 'Error') {
        setError(data.Error || 'Failed to get challenge');
        setStatus('idle');
        return;
      }

      if (data.challenge) {
        setChallenge(data.challenge);
        setStatus('ready_to_sign');
      } else {
        setError('Unexpected response from server');
        setStatus('idle');
      }
    } catch (err) {
      setError('Failed to connect to server: ' + err.message);
      setStatus('idle');
    }
  };

  /**
   * Handle MetaMask signing and submit to backend
   */
  const handleSign = async () => {
    try {
      setStatus('signing');
      setError('');
      
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed. Please install MetaMask to continue.');
      }
      
      // Request MetaMask connection if not already connected
      const provider = new ethers.BrowserProvider(window.ethereum);
      await provider.send("eth_requestAccounts", []);
      const signer = await provider.getSigner();
      const address = await signer.getAddress();
      
      // Sign the challenge message
      const signature = await signer.signMessage(challenge);
      
      // Submit signature to backend
      const response = await fetch(`${apiUrl}/api/WebQueryStatus?code=${webApiCode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          FunctionArgument: {
            RequestType: 'CheckWalletStatus',
            wallet_address: address,
            challenge: challenge,
            signature: signature
          }
        })
      });
      
      const result = await response.json();
      
      // Handle response
      if (result.Reason === 'Error') {
        setError(result.Error || 'Status check failed');
        setStatusColor('gray');
        setStatus('error');
        return;
      }
      
      if (result.status) {
        // Update status color based on result
        if (result.status === 'C') {
          setStatusColor('green');
        } else if (result.status === 'R') {
          setStatusColor('blue');
        } else {
          setStatusColor('gray');
        }
        
        // Update last checked time
        if (result.last_query_date) {
          setLastChecked(new Date(result.last_query_date));
        }
        
        // Update cached flag
        setIsCached(result.cached === true);
        
        setStatus('success');
        setChallenge(null);
      } else {
        setError('Unexpected response from server');
        setStatusColor('gray');
        setStatus('error');
      }
    } catch (err) {
      if (err.code === 4001) {
        // User rejected the signature request
        setError('Signature request was rejected');
        setStatus('ready_to_sign');
      } else {
        setError('Signing failed: ' + err.message);
        setStatusColor('gray');
        setStatus('error');
      }
    }
  };

  /**
   * Cancel the signing process
   */
  const handleCancel = () => {
    setChallenge(null);
    setStatus('idle');
    setError('');
  };

  /**
   * Handle terms checkbox change
   */
  const handleTermsChange = (e) => {
    const checked = e.target.checked;
    setTermsAccepted(checked);
  };

  return (
    <div className="user-status-container">
      <div className="user-status-card">
        <button onClick={onBack} className="btn-back">
          ← Back to login
        </button>

        <div className="status-display">
          <div className="status-header">
            <span className="status-label">User Status</span>
            <span className={`status-dot ${statusColor}`}>●</span>
          </div>
          
          {status === 'success' && statusColor !== 'gray' && (
            <div className="status-hint">
              {statusColor === 'green' && (
                <span className="hint-text hint-c">MODE: 🎨</span>
              )}
              {statusColor === 'blue' && (
                <span className="hint-text hint-r">MODE: 🧭</span>
              )}
            </div>
          )}
          
          {lastChecked && (
            <div className="status-info">
              <p className="last-checked">
                Last checked: {lastChecked.toLocaleString()}
              </p>
              {isCached && (
                <p className="cache-warning">
                  Data might be from cache, to fetch latest data please wait a few minutes before rechecking
                </p>
              )}
            </div>
          )}
        </div>

        {/* Wallet Status */}
        {!walletAddress && status === 'idle' && (
          <div className="wallet-section">
            <p className="wallet-prompt">Connect your wallet to check status</p>
            <button onClick={handleConnectWallet} className="btn-connect">
              Connect Wallet
            </button>
          </div>
        )}

        {/* Connected Wallet Display */}
        {walletAddress && (
          <div className="wallet-connected-info">
            <span className="wallet-label">Connected:</span>
            <span className="wallet-address">{walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</span>
          </div>
        )}

        {walletAddress && status === 'idle' && isFirstTime() && (
          <div className="terms-agreement">
            <div className="terms-checkbox-wrapper">
              <input
                type="checkbox"
                id="terms-checkbox"
                className="terms-checkbox"
                checked={termsAccepted}
                onChange={handleTermsChange}
              />
              <label htmlFor="terms-checkbox" className="terms-label">
                I have read and agree to the{' '}
                <a href="/terms.html" target="_blank" rel="noopener noreferrer">
                  Terms of Use
                </a>
                {' '}and{' '}
                <a href="/privacy.html" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
              </label>
            </div>
          </div>
        )}

        {/* Check Status Button */}
        {walletAddress && status === 'idle' && (
          <button 
            onClick={handleCheckStatus} 
            className="btn-check"
            disabled={!termsAccepted}
          >
            Check Status
          </button>
        )}

        {/* Loading State */}
        {status === 'loading' && (
          <div className="status-message">
            <div className="spinner"></div>
            <p>Requesting challenge...</p>
          </div>
        )}

        {/* Ready to Sign */}
        {status === 'ready_to_sign' && (
          <div className="sign-section">
            <div className="security-warning">
              <div className="warning-header">
                <span className="warning-icon">⚠️</span>
                <strong>Important: Verify Message Format</strong>
              </div>
              <div className="warning-content">
                <p>You will be asked to sign a message with the following format:</p>
                <div className="expected-format">
                  [EXA] Please sign to check status, Date: &lt;timestamp&gt;
                </div>
                <p>Only sign if the message matches this format. If it doesn’t, cancel the request and open a ticket at the help desk.
                </p>
              </div>
            </div>
            
            <div className="challenge-display">
              <label className="challenge-label">Message:</label>
              <div className="challenge-text">
                {challenge}
              </div>
            </div>
            
            <button onClick={handleSign} className="btn-sign">
              Sign with MetaMask
            </button>
            <button onClick={handleCancel} className="btn-cancel">
              Cancel
            </button>
          </div>
        )}

        {/* Signing State */}
        {status === 'signing' && (
          <div className="sign-section">
            <div className="security-warning">
              <div className="warning-header">
                <span className="warning-icon">⚠️</span>
                <strong>Important: Verify Message Format</strong>
              </div>
              <div className="warning-content">
                <p>You will be asked to sign a message with the following format:</p>
                <div className="expected-format">
                  [EXA] Please sign to check status, Date: &lt;timestamp&gt;
                </div>
                <p>Only sign if the message matches this format. If it doesn't, cancel the request and open a ticket at the help desk.
                </p>
              </div>
            </div>
            
            <div className="status-message">
              <div className="spinner"></div>
              <p>Please check MetaMask and sign the message...</p>
            </div>
            
            <div className="challenge-display">
              <label className="challenge-label">Message:</label>
              <div className="challenge-text">
                {challenge}
              </div>
            </div>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && (
          <div className="success-section">
            <p className="success-message">✓ Status check complete!</p>
            <button onClick={() => setStatus('idle')} className="btn-check-again">
              Check Again
            </button>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}
      </div>

      <footer className="footer">
        <div className="footer-legal">
          <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms of Use</a>
          {' | '}
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        </div>
      </footer>
    </div>
  );
}

export default UserStatus;
